import { supabase } from '../lib/supabase';

/**
 * Service for Student Songs Discovery Page
 * Handles fetching songs data with categorization:
 * - Continue Learning (songs with user progress)
 * - Popular Songs
 * - Recently Updated Songs
 * - Recently Added Songs
 */

export const studentSongsDiscoveryService = {
  /**
   * Get songs the user is currently learning
   */
  async getContinueLearningSongs(userId, limit = 12) {
    try {
      const { data: progressData, error: progressError } = await supabase?.from('user_lesson_progress')?.select('song_id')?.eq('user_id', userId)?.in('lesson_status', ['started', 'available'])?.limit(limit);

      if (progressError) throw progressError;

      const songIds = progressData?.map(p => p?.song_id)?.filter(Boolean) || [];

      if (songIds?.length === 0) return [];

      // Fetch song details with musical info
      const { data: songs, error: songsError } = await supabase?.from('songs')?.select(`
          id,
          title,
          act_number,
          musical_id,
          musicals (
            title,
            poster_url
          )
        `)?.in('id', songIds)?.limit(limit);

      if (songsError) throw songsError;

      return songs?.map(song => ({
        id: song?.id,
        title: song?.title,
        actNumber: song?.act_number,
        musicalId: song?.musical_id,
        musicalTitle: song?.musicals?.title,
        musicalPoster: song?.musicals?.poster_url
      })) || [];
    } catch (error) {
      console.error('Error fetching continue learning songs:', error);
      throw error;
    }
  },

  /**
   * Get popular songs (most favorited)
   */
  async getPopularSongs(limit = 12) {
    try {
      // Count favorites per song
      const { data: favorites, error: favError } = await supabase?.from('song_favorites')?.select('song_id');

      if (favError) throw favError;

      // Count occurrences
      const counts = {};
      favorites?.forEach(fav => {
        counts[fav?.song_id] = (counts?.[fav?.song_id] || 0) + 1;
      });

      // Sort by count and get top song IDs
      const topSongIds = Object.entries(counts)?.sort(([, a], [, b]) => b - a)?.slice(0, limit * 2)?.map(([id]) => id);

      if (topSongIds?.length === 0) {
        // Fallback to recently added songs
        const { data: allSongs, error: allError } = await supabase?.from('songs')?.select(`
            id,
            title,
            act_number,
            musical_id,
            musicals (
              title,
              poster_url
            )
          `)?.order('created_at', { ascending: false })?.limit(limit);

        if (allError) throw allError;

        return allSongs?.map(song => ({
          id: song?.id,
          title: song?.title,
          actNumber: song?.act_number,
          musicalId: song?.musical_id,
          musicalTitle: song?.musicals?.title,
          musicalPoster: song?.musicals?.poster_url
        })) || [];
      }

      const { data: songs, error: songsError } = await supabase?.from('songs')?.select(`
          id,
          title,
          act_number,
          musical_id,
          musicals (
            title,
            poster_url
          )
        `)?.in('id', topSongIds)?.limit(limit);

      if (songsError) throw songsError;

      return songs?.map(song => ({
        id: song?.id,
        title: song?.title,
        actNumber: song?.act_number,
        musicalId: song?.musical_id,
        musicalTitle: song?.musicals?.title,
        musicalPoster: song?.musicals?.poster_url
      })) || [];
    } catch (error) {
      console.error('Error fetching popular songs:', error);
      throw error;
    }
  },

  /**
   * Get recently updated songs
   */
  async getRecentlyUpdatedSongs(limit = 12) {
    try {
      const { data, error } = await supabase?.from('songs')?.select(`
          id,
          title,
          act_number,
          musical_id,
          updated_at,
          musicals (
            title,
            poster_url
          )
        `)?.order('updated_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(song => ({
        id: song?.id,
        title: song?.title,
        actNumber: song?.act_number,
        musicalId: song?.musical_id,
        musicalTitle: song?.musicals?.title,
        musicalPoster: song?.musicals?.poster_url
      })) || [];
    } catch (error) {
      console.error('Error fetching recently updated songs:', error);
      throw error;
    }
  },

  /**
   * Get recently added songs
   */
  async getRecentlyAddedSongs(limit = 12) {
    try {
      const { data, error } = await supabase?.from('songs')?.select(`
          id,
          title,
          act_number,
          musical_id,
          created_at,
          musicals (
            title,
            poster_url
          )
        `)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(song => ({
        id: song?.id,
        title: song?.title,
        actNumber: song?.act_number,
        musicalId: song?.musical_id,
        musicalTitle: song?.musicals?.title,
        musicalPoster: song?.musicals?.poster_url
      })) || [];
    } catch (error) {
      console.error('Error fetching recently added songs:', error);
      throw error;
    }
  },

  /**
   * Search songs
   */
  async searchSongs(query) {
    try {
      const { data, error } = await supabase?.from('songs')?.select(`
          id,
          title,
          act_number,
          musical_id,
          musicals (
            title,
            poster_url
          )
        `)?.ilike('title', `%${query}%`)?.order('title', { ascending: true })?.limit(20);

      if (error) throw error;

      return data?.map(song => ({
        id: song?.id,
        title: song?.title,
        actNumber: song?.act_number,
        musicalId: song?.musical_id,
        musicalTitle: song?.musicals?.title,
        musicalPoster: song?.musicals?.poster_url
      })) || [];
    } catch (error) {
      console.error('Error searching songs:', error);
      throw error;
    }
  }
};